import { Checkbox, Tooltip } from '@innovationdepartment/proxima-ui';
import {
  AUDIENCE_FIELDS,
  type AudienceFieldName,
  dependsOnHint,
  isOfferedField
} from '../lib/audienceFields';
import { FILTER_GROUPS } from '../lib/filters';
import SupportBadge from './FilterRules/SupportBadge';
import {
  MapperDomain,
  MapperDomainTitle,
  MapperField,
  MapperMarks,
  SidebarCount
} from './form.styles';

type ColumnMapperProps = {
  checkedFields: Set<string>;
  /** The purchase window rule's value, or null without one; names the window in dependency notes. */
  lookbackDays: number | null;
  onToggle: (name: AudienceFieldName) => void;
};

/**
 * Checkable list of filterable columns by domain; checking adds a rule, unchecking removes it.
 *
 * - Only offered columns are listed, and a domain with none is left out.
 * - Columns within a domain are sorted by label.
 * - Checking a negatable column adds its include rule; its row adds the exclude side.
 * - Unchecking removes every rule of the column.
 * - Proposed and limited columns carry a badge, and dependency notes sit in an info tooltip.
 * - Hints and exclusion notes show on the rule row only.
 */
const ColumnMapper = ({ checkedFields, lookbackDays, onToggle }: ColumnMapperProps) => (
  <>
    {FILTER_GROUPS.map(group => {
      const fields = AUDIENCE_FIELDS.filter(
        field => field.domain === group.id && isOfferedField(field)
      ).toSorted((a, b) => a.label.localeCompare(b.label));
      if (fields.length === 0) return null;
      const checked = fields.filter(field => checkedFields.has(field.name)).length;
      return (
        <MapperDomain key={group.id}>
          <MapperDomainTitle>
            {group.label}
            {checked > 0 ? (
              <SidebarCount aria-label={`${checked} active`}>{checked}</SidebarCount>
            ) : null}
          </MapperDomainTitle>
          {fields.map(field => {
            const dependency = dependsOnHint(field, lookbackDays);
            return (
              <MapperField key={field.name} data-column={field.name}>
                <Checkbox
                  controlled
                  checked={checkedFields.has(field.name)}
                  onChange={() => onToggle(field.name)}
                  label={field.label}
                />
                <MapperMarks>
                  <SupportBadge support={field.backendSupport} />
                  {dependency ? <Tooltip info title={dependency} placement="right" /> : null}
                </MapperMarks>
              </MapperField>
            );
          })}
        </MapperDomain>
      );
    })}
  </>
);

export default ColumnMapper;
